<template>
  <BaseMenu
    :items="items"
    :title="$t('search-filters.status.name')"
    :selection="selected"
    :attach="attach"
    is-unclearable
    @select-item="updateStatuses"
    @click-outside="applyImmediately"
    :searchAllowed="searchAllowed"
    :is-disabled="isDisabled || loading"
    ref="baseStatusMenu"
  >
    <template v-slot:activator="{ on }">
      <div v-if="showIcon && !loading">
        <!-- The status filter chip's icon: the tag is the labels'. -->
        <BaseRoundButton icon="label-variant-outline" is-white :tooltip-text="$t('actions.change-status')" />
      </div>
      <div
        v-else-if="currentStatus"
        class="base-opportunity-status-menu__current-badge"
        :class="{ 'base-opportunity-status-menu__current-badge--loading': loading }"
        v-tooltip="{ content: tooltipContent, placement: tooltipPlacement, offset: '8px', delay: 800 }"
      >
        <BaseStatusBadge :text="currentStatus.name" :color="currentStatus.color" isActionable />
        <div v-if="loading" class="base-opportunity-status-menu__spinner">
          <BaseCircularLoadingBar :size="12" :width="2" color="accent" />
        </div>
      </div>
      <div v-else-if="loading" class="base-opportunity-status-menu__activator">
        <BaseCircularLoadingBar :size="12" :width="2" color="accent" />
      </div>
      <!-- In case where we want to bulk action for exemple -->
      <div v-else class="base-menu-activator" v-on="on">
        <div class="base-menu-activator__content">
          <div>{{ $t('actions.change-status') }}</div>
          <BaseIcon name="chevron-down" />
        </div>
      </div>
    </template>
    <template v-slot:default="{ item }">
      <div class="items">
        <div class="items__badge">
          <BaseStatusBadge :text="item.name" :color="item.color" />
        </div>
      </div>
    </template>
    <template #sheet-choice="{ item }">
      <span class="base-bottom-sheet__swatch" :style="{ background: item.color }"></span>
      {{ item.name }}
    </template>
  </BaseMenu>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import BaseMenu from '@/components/base/BaseMenu';
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar.vue';
import BaseStatusBadge from '@/components/base/BaseStatusBadge.vue';
import BaseRoundButton from './BaseRoundButton.vue';

export default {
  name: 'BaseStatusMenu',
  components: {
    BaseIcon,
    BaseCircularLoadingBar,
    BaseStatusBadge,
    BaseMenu,
    BaseRoundButton,
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
    currentStatus: {
      type: Object,
    },
    tooltipPlacement: {
      type: String,
      required: false,
      default: 'top',
    },
    searchAllowed: {
      type: Boolean,
      default: false,
    },
    attach: {
      type: String,
    },
    showIcon: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      innerValue: null,
      selected: [],
      isDirty: false,
      loading: false,
    };
  },
  computed: {
    tooltipContent() {
      return this.$t('quotes.change-status');
    },
  },
  methods: {
    updateStatuses(item) {
      this.innerValue = item;
      this.applyChanges();
      this.closeMenu();
    },
    applyImmediately() {
      if (!this.isDirty) return;
      this.debounce(null, 0, true);
      this.applyChanges();
    },
    applyChanges() {
      this.$emit('change', this.innerValue);
      this.isDirty = false;
    },
    closeMenu() {
      this.selected = [];
      this.isDirty = false;
      this.$refs.baseStatusMenu.menu = false;
    },
    // This is called by other components
    setLoading(loading) {
      this.loading = loading;
    },
  },
  watch: {
    currentStatus() {
      this.innerValue = this.currentStatus;
      if (this.innerValue != null) {
        this.selected = [this.innerValue];
      } else {
        this.selected = [];
      }
    },
  },
  created() {
    this.innerValue = this.currentStatus;
    if (this.innerValue != null) {
      this.selected = [this.innerValue];
    }
  },
  beforeDestroy() {
    this.innerValue = null;
    this.selected = [];
  },
};
</script>

<style lang="scss">
.base-opportunity-status-menu {
  &__activator {
    align-items: center;
    background-color: $color-neutral-pale;
    color: $color-neutral;
    display: inline-flex;
    flex: none;
    justify-content: center;
    padding: 4px 8px;
    border-radius: $border-radius-small;
    outline: 1px dashed $color-neutral;
  }

  &__current-badge {
    cursor: pointer;
    display: inline-flex;
    width: fit-content;
    max-width: 100%;
    position: relative;

    &--loading {
      cursor: default;
      pointer-events: none;

      .opportunity-status-badge {
        opacity: 0.5;
      }
    }
  }

  &__spinner {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  &__activator,
  &__spinner {
    .base-circular-loading-bar,
    .base-circular-loading-bar__content {
      height: auto;
      gap: 0;
    }
  }
}

.base-menu__activator:has(.base-opportunity-status-menu__current-badge),
.base-menu__activator:has(.base-opportunity-status-menu__activator) {
  display: inline-flex;
  width: fit-content;
  max-width: 100%;
}

.items {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &__badge {
    width: fit-content;
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.base-menu-activator {
  display: flex;
  padding: 6px 12px;
  border-radius: $border-radius-root !important;
  border: 1px solid $color-border;
  box-shadow: $shadow-dropdown;
  color: $color-normal;
  transition: all 200ms ease-in;
  cursor: pointer;
  width: max-content;

  &__content {
    gap: 8px;
    align-items: center;
    display: flex;
  }

  &:hover {
    background: $color-hover !important;
    transition: all 200ms ease-in;
  }

  .base-icon {
    color: $color-normal;
    height: 16px;
    width: 16px;
  }

  span {
    pointer-events: none;
  }
}
</style>
